/*
 *
 * File: ChooseCarCategoryOfferFixPrice.jsx
 *
 * Purpose: Choose car category offer component for fix price routes.
 *
 * Author:  Thomas Burgard (Software development)
 * Created: 28.03.2023
 *
 * Copyright (c) DrivePartner GmbH 2022
 *
 */

import { React, useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";

//--------------------------------------------------------------
//--- CSS styles -----------------------------------------------
//--------------------------------------------------------------
const carCategoryBoxStyleNormal = {
  border: "1px solid #ced4da",
  borderRadius: "5px",
  padding: "10px",
  paddingLeft: "7px",
  paddingRight: "7px",
  paddingBottom: "11px",
  marginBottom: "11px",
  backgroundColor: "#fafafa",
  cursor: "pointer",
};
const carCategoryBoxStyleSelected = {
  border: "2px solid #4d5db5",
  borderRadius: "5px",
  padding: "10px",
  paddingLeft: "7px",
  paddingRight: "7px",
  paddingBottom: "11px",
  marginBottom: "10px",
  backgroundColor: "#edefff",
};
const carCategoryTextStyle = {
  fontSize: "0.9rem",
  fontWeight: "bold",  
};
const carCategoryFareStyle = {
  fontSize: "0.9rem",
  fontWeight: "bold",
  textAlign: "right",
};
const carCategoryModelsStyle = {
  fontSize: "0.9rem",
  paddingTop: "5px",
  paddingBottom: "5px",
  paddingLeft: "3PX",
};
const tableStyle = {
  border: "0px",
  width: "100%",
};
//--- End CSS styles -------------------------------------------

/**
 * Implementation of choose car category offer component for
 * fix price routes booking. Show all configured car category
 * price information in the booking screen.
 *
 * @param {*} props (userData,
 *                   selectedFixRoute,
 *                   calcFaresFinished,
 *                   userRegistrationDataObj,
 *                   choosenCarOffer)
 * @returns JSX-HTML-DOM
 */
const ChooseCarCategoryOfferFixPrice = (props) => {
  const selectedFixRoute = props.selectedFixRoute;
  const { t, i18n } = useTranslation("common");
  const [chooseCarCategoryBoxIndex, setChooseCarCategoryBoxIndex] =
    useState(-1);
  const lastRoute = useRef("");
  const newRouteSelected = useRef(false);
  const carCategoryCounter = useRef(0);
  let fixPriceRoutePriceArray = [];
  let fixPriceRouteCarModelArray = [];
  let carCategoryArray = [];
  let carCategoryAlternativeNameArray = [];

  if (selectedFixRoute !== null) {
    // null is set if nothing is selected in the Listbox
    if (lastRoute.current !== selectedFixRoute["ROUTE_NAME"]) {
      // New route selected in the Listbox
      newRouteSelected.current = true;
      carCategoryCounter.current = 0;
      lastRoute.current = selectedFixRoute["ROUTE_NAME"];
    } else {
      newRouteSelected.current = false;
    }
  }

  //--------------------------------------------------------------
  //--- Will be called after "first" rendering! ------------------
  //--------------------------------------------------------------
  useEffect(() => {
    if (newRouteSelected.current) setChooseCarCategoryBoxIndex(-1); // Reset box selection index
  });

  /**
   * Check fix price route.
   */
  const checkFixPriceRoute = () => {
    if (selectedFixRoute["PRICE_BUSINESS"] !== "0.00") {
      carCategoryArray.push("Limousine Business");

      if (
        selectedFixRoute["LIM_BUSINESS_ALTERNATIVE_NAME"] !== "" &&
        selectedFixRoute["LIM_BUSINESS_ALTERNATIVE_NAME"] !== null
      ) {
        carCategoryAlternativeNameArray.push(
          selectedFixRoute["LIM_BUSINESS_ALTERNATIVE_NAME"]
        );
      } else {
        carCategoryAlternativeNameArray.push("");
      }

      fixPriceRoutePriceArray.push(selectedFixRoute["PRICE_BUSINESS"]);
      fixPriceRouteCarModelArray.push(selectedFixRoute["CARMODELS_BUSINESS"]);
    }

    if (selectedFixRoute["PRICE_FIRSTCLASS"] !== "0.00") {
      carCategoryArray.push("Business First Class");

      if (
        selectedFixRoute["BUSINESS_FIRST_CLASS_ALTERNATIVE_NAME"] !== "" &&
        selectedFixRoute["BUSINESS_FIRST_CLASS_ALTERNATIVE_NAME"] !== null
      ) {
        carCategoryAlternativeNameArray.push(
          selectedFixRoute["BUSINESS_FIRST_CLASS_ALTERNATIVE_NAME"]
        );
      } else {
        carCategoryAlternativeNameArray.push("");
      }

      fixPriceRoutePriceArray.push(selectedFixRoute["PRICE_FIRSTCLASS"]);
      fixPriceRouteCarModelArray.push(selectedFixRoute["CARMODELS_FIRSTCLASS"]);
    }

    if (selectedFixRoute["PRICE_VAN"] !== "0.00") {
      carCategoryArray.push("Business Van");

      if (
        selectedFixRoute["BUSINESS_VAN_ALTERNATIVE_NAME"] !== "" &&
        selectedFixRoute["BUSINESS_VAN_ALTERNATIVE_NAME"] !== null
      ) {
        carCategoryAlternativeNameArray.push(
          selectedFixRoute["BUSINESS_VAN_ALTERNATIVE_NAME"]
        );
      } else {
        carCategoryAlternativeNameArray.push("");
      }

      fixPriceRoutePriceArray.push(selectedFixRoute["PRICE_VAN"]);
      fixPriceRouteCarModelArray.push(selectedFixRoute["CARMODELS_VAN"]);
    }

    if (selectedFixRoute["PRICE_BUSXL"] !== "0.00") {
      carCategoryArray.push("Bus XL");

      if (
        selectedFixRoute["BUSXL_ALTERNATIVE_NAME"] !== "" &&
        selectedFixRoute["BUSXL_ALTERNATIVE_NAME"] !== null
      ) {
        carCategoryAlternativeNameArray.push(
          selectedFixRoute["BUSXL_ALTERNATIVE_NAME"]
        );
      } else {
        carCategoryAlternativeNameArray.push("");
      }

      fixPriceRoutePriceArray.push(selectedFixRoute["PRICE_BUSXL"]);
      fixPriceRouteCarModelArray.push(selectedFixRoute["CARMODELS_BUSXL"]);
    }

    return fixPriceRoutePriceArray.map((element, index) =>
      showFixPriceRouteInfoBox(
        element,
        index,
        carCategoryArray,
        carCategoryAlternativeNameArray,
        fixPriceRouteCarModelArray
      )
    );
  };

  /**
   *
   * @param {*} element fixPriceRoutePriceArray element
   * @param {*} index   index for access to fixPriceRouteCarModelArray
   * @param {*} carCategoryArray
   * @param {*} carCategoryAlternativeNameArray
   * @param {*} fixPriceRouteCarModelArray
   * @returns JSX-Code for info box for selected fix price route
   */
  const showFixPriceRouteInfoBox = (
    element,
    index,
    carCategoryArray,
    carCategoryAlternativeNameArray,
    fixPriceRouteCarModelArray
  ) => {
    let carCategoryHeaderText = "";

    return (
      <div
        style={setStyle(index, fixPriceRoutePriceArray.length)}
        onClick={() =>
          boxClickHandler(
            index,
            fixPriceRoutePriceArray[index],
            carCategoryArray[index],
            carCategoryAlternativeNameArray[index],
            fixPriceRouteCarModelArray[index]
          )
        }
      >
        <table style={tableStyle}>
          <tr>
            <td style={carCategoryTextStyle}>
              {carCategoryAlternativeNameArray[index] === ""
                ? carCategoryArray[index]
                : carCategoryAlternativeNameArray[index]}
            </td>
            <td style={carCategoryFareStyle}>€ {element}</td>
          </tr>
        </table>
        <span style={carCategoryModelsStyle}>
          {fixPriceRouteCarModelArray[index]}
        </span>
      </div>
    );
  };

  /**
   * Car fix price route box click handler.
   *
   * @param {*} selBoxIndex Selected car category box index
   * @param {*} price Selected car category fix price
   * @param {*} carCategory Selected car category
   * @param {*} carCategoryAlternativeName Selected car category alternative name
   * @param {*} carModel Selected car category model
   */
  const boxClickHandler = (
    selBoxIndex,
    price,
    carCategory,
    carCategoryAlternativeName,
    carModel
  ) => {
    setChooseCarCategoryBoxIndex(selBoxIndex);
    props.choosenCarOffer(
      price,
      carCategory,
      carCategoryAlternativeName,
      carModel
    ); // Call function in parent component with given car category (car offer) & fix price!
  };

  /**
   * Set the selected box style.
   *
   * @param {*} selBoxIndex
   * @param {*} nbrOfCarCategories
   * @returns box CSS style
   */
  const setStyle = (selBoxIndex, nbrOfCarCategories) => {
    if (newRouteSelected.current) {
      if (carCategoryCounter.current <= nbrOfCarCategories) {
        carCategoryCounter.current++;
        return carCategoryBoxStyleNormal;
      }
    } else {
      if (selBoxIndex === chooseCarCategoryBoxIndex) {
        return carCategoryBoxStyleSelected;
      } else {
        return carCategoryBoxStyleNormal;
      }
    }
  };

  //--------------------------------------------------------------
  //--- Returns JSX code for "ChooseCarCategoryOfferFixPrice" ----
  //--------------------------------------------------------------
  return (
    <>
      {props.calcFaresFinished && props.userRegistrationDataObj.tariff === 2 ? (
        <>
          <label style={{ fontWeight: "bold" }}>
            {t("formular.header-choose-offer")}
          </label>
          <div className="mt-2" />
          {checkFixPriceRoute()}
        </>
      ) : null}
    </>
  );
};

export default ChooseCarCategoryOfferFixPrice;
